import { Trans, useLingui } from "@lingui/react/macro";
import type { AskerAccessLevel, BotAsker } from "@rakazo/contracts";
import { writeSaved } from "@rakazo/core/query";
import { Button, NativeSelect, NativeSelectOption } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";
import { SettingsGroup, SettingsRow } from "./SettingsRows";

/**
 * Who in the Space may ask a shared bot for what. A teammate's first ask waits here for the
 * owner; the owner lets them in with a level, or declines. Hidden until someone has asked.
 */
export function BotAskersSettings({ botId }: { botId: string }) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const input = { botId };
  const list = useQuery(queries.botAskers.list.queryOptions({ input }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The level picked for someone not yet let in; saved when they are approved.
  const [drafts, setDrafts] = useState<Record<string, AskerAccessLevel>>({});
  const askers = list.data ?? [];
  if (askers.length === 0) return null;
  const levels: Array<{ value: AskerAccessLevel; label: string }> = [
    { value: "chat", label: t`Chat only` },
    { value: "actions", label: t`Run actions` },
    { value: "connections", label: t`Use connections` },
  ];

  async function decide(
    asker: BotAsker,
    status: "approved" | "declined",
    access: AskerAccessLevel = asker.access,
  ) {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await rpc.botAskers.decide({ botId, userId: asker.userId, status, access });
      await writeSaved(queryClient, queries.botAskers.list.queryKey({ input }), (current) =>
        (current ?? []).map((other) => (other.userId === saved.userId ? saved : other)),
      );
    } catch (err) {
      setError(errorText(err, t`Could not save`));
    } finally {
      setSaving(false);
    }
  }

  const ordered = [...askers].sort(
    (left, right) => Number(right.status === "pending") - Number(left.status === "pending"),
  );
  return (
    <SettingsGroup title={<Trans>People who ask</Trans>} data-testid="bot-askers">
      {ordered.map((asker) => (
        <SettingsRow
          key={asker.userId}
          label={
            <span className="flex flex-col">
              <span>{asker.name || asker.email}</span>
              {asker.status === "declined" ? (
                <span className="text-[13px] text-muted-foreground">
                  <Trans>Declined</Trans>
                </span>
              ) : null}
            </span>
          }
          control={
            <>
              <NativeSelect
                aria-label={t`What ${asker.name || asker.email} may ask for`}
                value={
                  asker.status === "approved" ? asker.access : (drafts[asker.userId] ?? "chat")
                }
                disabled={saving}
                onChange={(event) => {
                  const access = event.target.value as AskerAccessLevel;
                  if (asker.status === "approved") void decide(asker, "approved", access);
                  else setDrafts((current) => ({ ...current, [asker.userId]: access }));
                }}
              >
                {levels.map((level) => (
                  <NativeSelectOption key={level.value} value={level.value}>
                    {level.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              {asker.status !== "approved" ? (
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={() => void decide(asker, "approved", drafts[asker.userId] ?? "chat")}
                >
                  <Trans>Approve</Trans>
                </Button>
              ) : null}
              {asker.status !== "declined" ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saving}
                  onClick={() => void decide(asker, "declined")}
                >
                  <Trans>Decline</Trans>
                </Button>
              ) : null}
            </>
          }
        />
      ))}
      {error ? <p className="px-4 py-2 text-[13px] text-destructive">{error}</p> : null}
    </SettingsGroup>
  );
}
